import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
} from "ra-core";

import type { CrmDataProvider } from "../providers/types";
import {
  buildApproveDecision,
  buildProposalQueue,
  buildRejectDecision,
  interpretApproveResponse,
  interpretRejectResponse,
  toProposalActionRequest,
  type AgentWriteProposal,
  type ProposalOutcome,
} from "./proposalReview";

/** Plenty for a queue that is reviewed by a human, not swiped at contact-deck volume. */
const PAGE_SIZE = 20;

/** Refill once the buffer drops this low, so a long backlog does not dead-end at 20. */
const REFILL_AT = 5;

/**
 * An outcome pinned to the proposal it came from. `current` stays on that
 * proposal until this is dismissed — see the module comment on why nothing
 * is consumed the moment a response arrives.
 */
interface PendingOutcome {
  proposalId: number;
  result: ProposalOutcome;
}

export interface ProposalQueue {
  current: AgentWriteProposal | undefined;
  next: AgentWriteProposal | undefined;
  /** Set once `current`'s approve/reject has returned; not yet dismissed. */
  outcome: ProposalOutcome | undefined;
  /** True while an approve/reject request is in flight for `current`. */
  isWorking: boolean;
  isPending: boolean;
  /** A real fetch failure — RLS, network, a table PostgREST does not know. */
  error: unknown;
  /** True in FakeRest demo mode, which has no edge function to approve through. */
  isDemo: boolean;
  /** True once there is nothing pending and nothing in flight. */
  isExhausted: boolean;
  remaining: number | undefined;
  approve: () => void;
  reject: (reason: string) => void;
  /** Leaves the proposal pending and moves on. Writes nothing — see proposalReview.ts. */
  skip: () => void;
  /** Acknowledge `outcome` and reveal the next card. */
  continueAfterOutcome: () => void;
}

/**
 * The queue behind the proposal review deck (#24's last open box).
 *
 * Reads through the ordinary data provider — `agent_write_proposals` is a
 * plain PostgREST-backed resource, granted `select` to `authenticated` with a
 * read-everything RLS policy (06_grants.sql), so listing what is pending
 * needs no edge function. Only `approve`/`reject` leave this path, through
 * `dataProvider.reviewProposal`, which is the one door that can actually
 * replay a stored tool call — see that method's comment for why
 * `dataProvider.update` is not an option here.
 *
 * Unlike useSwipeQueue, a decision here is never optimistic and there is no
 * undo: approving is a real, possibly-failing write against a live record,
 * so the card holds its ground showing exactly what happened until the human
 * dismisses it, rather than vanishing the instant a button is pressed.
 */
export function useProposalQueue(): ProposalQueue {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const isDemo = import.meta.env.VITE_IS_DEMO === "true";
  const reviewedBy = identity?.fullName ?? "";

  const [consumed, setConsumed] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const [isWorking, setIsWorking] = useState(false);
  const [pendingOutcome, setPendingOutcome] = useState<
    PendingOutcome | undefined
  >();

  const { data, total, isPending, error, refetch } =
    useGetList<AgentWriteProposal>(
      "agent_write_proposals",
      {
        pagination: { page: 1, perPage: PAGE_SIZE },
        sort: { field: "created_at", order: "ASC" },
        filter: { status: "pending" },
      },
      // Demo mode has no such table to read through PostgREST — do not even
      // ask, so the empty state says "not available here" rather than
      // surfacing whatever ra-data-fakerest does with an unknown resource.
      { enabled: !isDemo },
    );

  const buffer = useMemo(
    () => buildProposalQueue(data ?? [], consumed),
    [data, consumed],
  );

  const needsRefill =
    !isDemo &&
    !isPending &&
    !error &&
    buffer.length < REFILL_AT &&
    (total ?? 0) > buffer.length;
  useEffect(() => {
    if (needsRefill) void refetch();
  }, [needsRefill, refetch]);

  const current = buffer[0];
  const next = buffer[1];

  const consume = useCallback((id: number) => {
    setConsumed((prev) => {
      const set = new Set(prev);
      set.add(id);
      return set;
    });
  }, []);

  const skip = useCallback(() => {
    if (!current || isWorking) return;
    consume(current.id);
  }, [current, isWorking, consume]);

  const approve = useCallback(() => {
    if (!current || isWorking) return;
    const built = buildApproveDecision(current.id, reviewedBy);
    if (!built.ok) {
      notify(built.error, { type: "warning" });
      return;
    }
    const body = toProposalActionRequest(built.decision);
    if (!body) return; // Never happens for "approve"; keeps the type honest.
    const proposalId = current.id;
    setIsWorking(true);
    dataProvider
      .reviewProposal(body)
      .then(({ ok, body: responseBody }) => {
        setPendingOutcome({
          proposalId,
          result: interpretApproveResponse(responseBody, ok),
        });
      })
      .catch((e: unknown) => {
        setPendingOutcome({
          proposalId,
          result: {
            kind: "error",
            speech:
              e instanceof Error ? e.message : "The approval request failed.",
          },
        });
      })
      .finally(() => setIsWorking(false));
  }, [current, isWorking, reviewedBy, dataProvider, notify]);

  const reject = useCallback(
    (reason: string) => {
      if (!current || isWorking) return;
      const built = buildRejectDecision(current.id, reviewedBy, reason);
      if (!built.ok) {
        notify(built.error, { type: "warning" });
        return;
      }
      const body = toProposalActionRequest(built.decision);
      if (!body) return; // Never happens for "reject"; keeps the type honest.
      const proposalId = current.id;
      setIsWorking(true);
      dataProvider
        .reviewProposal(body)
        .then(({ ok, body: responseBody }) => {
          setPendingOutcome({
            proposalId,
            result: interpretRejectResponse(responseBody, ok),
          });
        })
        .catch((e: unknown) => {
          setPendingOutcome({
            proposalId,
            result: {
              kind: "error",
              speech:
                e instanceof Error
                  ? e.message
                  : "The rejection request failed.",
            },
          });
        })
        .finally(() => setIsWorking(false));
    },
    [current, isWorking, reviewedBy, dataProvider, notify],
  );

  const continueAfterOutcome = useCallback(() => {
    if (!pendingOutcome) return;
    consume(pendingOutcome.proposalId);
    setPendingOutcome(undefined);
  }, [pendingOutcome, consume]);

  const outcome =
    pendingOutcome && current && pendingOutcome.proposalId === current.id
      ? pendingOutcome.result
      : undefined;

  return {
    current,
    next,
    outcome,
    isWorking,
    isPending: isPending && !isDemo,
    error,
    isDemo,
    isExhausted:
      !isDemo &&
      !isPending &&
      !error &&
      buffer.length === 0 &&
      (total ?? 0) === 0,
    remaining: isDemo ? undefined : total,
    approve,
    reject,
    skip,
    continueAfterOutcome,
  };
}
